import React, { useContext, useEffect, useMemo, useState } from "react";
import compact from "lodash/compact";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";

import { SiteListObject } from "~/entities/siteEntities";
import ThemeContext from "~/contexts/ThemeContext";
import OrderContext from "~/contexts/OrderContext";
import { orderTypesForSource } from "~/helpers/orderHelper";
import {
  CategoryFontSize,
  StoresTitleFontSize,
} from "~/theme/models/themeModels";
import { hexColorToRGBA } from "~/helpers/colorHelpers";
import SettingsContext from "~/contexts/SettingsContext";
import GoogleContext from "~/contexts/GoogleContext";
import {
  distanceInMetersBetween,
  geocodeAddress,
  metersToLabel,
} from "~/helpers/coordsHelpers";
import { hasCoordinates } from "~/helpers/siteSort";

import SiteInfoModal from "../SiteInfoModal/SiteInfoModal";
import SiteItem from "../SiteItem/SiteItem";
import styles from "./SitesList.module.css";
import FilterDropdown from "~/components/FilterDropdown/FilterDropdown";
import SortIcon from "~/icons/SortIcon";
import SearchInput from "~/components/SearchInput/SearchInput";
import FiltersIcon from "~/icons/FiltersIcon";
import FiltersDrawer from "../FiltersDrawer/FiltersDrawer";
import { brandConfig } from "~/configs/brandConfigs";
import { Brand } from "~/entities/brandEntities";

enum Sort {
  A_Z = "A-Z",
  Z_A = "Z-A",
  DISTANCE = "Distance",
}

const sortOptions = [
  { value: Sort.A_Z, label: "Sort by A-Z" },
  { value: Sort.Z_A, label: "Sort by Z-A" },
  // { value: Sort.DISTANCE, label: "Sort by Distance" },
];

export type SiteWithPosition = SiteListObject & {
  position: google.maps.LatLngLiteral | null;
};

type SitesListProps = {
  sites: SiteListObject[];
  onSelectSite: (site: SiteListObject, orderTypeCode?: string | null) => void;
  prevSiteId?: string;
};

const PREV_SITE_SECTION_KEY = "__previous__";

const SitesList: React.FC<SitesListProps> = ({
  sites,
  onSelectSite,
  prevSiteId,
}) => {
  const [visibleSiteInfo, setVisibleSiteInfo] = useState<SiteListObject | null>(
    null,
  );
  const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(
    undefined,
  );
  // Filters
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedOrderTypeCode, setSelectedOrderTypeCode] = useState<
    string | null
  >(null);
  const [sortBy, setSortBy] = useState<string | null>(Sort.A_Z);
  const [searchValue, setSearchValue] = useState<string>("");
  const [visibleFiltersDrawer, setVisibleFiltersDrawer] = useState(false);

  const { template } = useContext(ThemeContext);
  const { orderSource } = useContext(OrderContext);
  const { address } = useContext(SettingsContext);
  const { isLoaded } = useContext(GoogleContext);

  // Positions come straight off the site record — the storefront never geocodes
  // a store. A site without configured coordinates is still listed, with no
  // position: no distance label, and last when sorting by distance.
  //
  // The one geocode below is for the CUSTOMER's saved address (the centre of the
  // distance sort), not for a store: at most one request per mount, and none at
  // all when no address is saved.
  useEffect(() => {
    if (!isLoaded) return;

    if (!address.trim()) {
      setCenter(undefined);
      return;
    }

    geocodeAddress(address).then((result) => {
      if (result) setCenter({ lat: result.latitude, lng: result.longitude });
    });
  }, [isLoaded, address]);

  const sitesWithPosition = useMemo<SiteWithPosition[]>(
    () =>
      sites.map((site) => ({
        ...site,
        position: hasCoordinates(site)
          ? {
              lat: site.address.latitude as number,
              lng: site.address.longitude as number,
            }
          : null,
      })),
    [sites],
  );

  const regionsOptions = useMemo(() => {
    const regions = uniq(compact(sites.map((site) => site.address.city))).sort(
      (a, b) => a.localeCompare(b),
    );
    return [
      { value: null, label: "All Regions" },
      ...regions.map((region) => ({ value: region, label: region })),
    ];
  }, [sites]);

  const orderTypesOptions = useMemo(() => {
    const orderTypes = uniqBy(
      sites.flatMap((site) =>
        orderTypesForSource(site.orderTypes, orderSource),
      ),
      "id",
    );

    return [
      { value: null, label: "All Order Types" },
      ...orderTypes.map((orderType) => ({
        value: orderType.code,
        label: orderType.name,
      })),
    ];
  }, [sites, orderSource]);

  // Grouped, filtered and sorted sites.
  const groupedFilteredAndSortedSites = useMemo(() => {
    const sortedSites = sitesWithPosition.sort((a, b) => {
      if (a.id === prevSiteId) {
        return -1;
      }
      if (b.id === prevSiteId) {
        return 1;
      }

      if (sortBy === Sort.A_Z) {
        return a.name.localeCompare(b.name);
      } else if (sortBy === Sort.Z_A) {
        return b.name.localeCompare(a.name);
      } else if (sortBy === Sort.DISTANCE) {
        if (!a.position && !b.position) return 0;
        if (!a.position) return 1;
        if (!b.position) return -1;

        const distanceA = distanceInMetersBetween(
          center?.lat ?? 0,
          center?.lng ?? 0,
          a.position?.lat ?? 0,
          a.position?.lng ?? 0,
        );
        const distanceB = distanceInMetersBetween(
          center?.lat ?? 0,
          center?.lng ?? 0,
          b.position?.lat ?? 0,
          b.position?.lng ?? 0,
        );

        return distanceA - distanceB;
      }

      return 0;
    });

    // Filtered sites
    const filteredSites = sortedSites.filter((site) => {
      const searchString = [
        site.name,
        site.address.address1,
        site.address.city,
        site.address.postalCode,
        site.address.state,
      ].join(", ");
      const isSearchMatch = searchString
        .toLowerCase()
        .includes(searchValue.toLowerCase().trim());
      const isRegion = !selectedRegion || site.address.city === selectedRegion;
      const isOrderType =
        !selectedOrderTypeCode ||
        orderTypesForSource(site.orderTypes, orderSource).some(
          (orderType) => orderType.code === selectedOrderTypeCode,
        );

      return isSearchMatch && isRegion && isOrderType;
    });

    // Grouped sites
    const groupedSites: Record<string, SiteWithPosition[]> =
      filteredSites.reduce(
        (groups, site) => {
          const key =
            brandConfig.Brand !== Brand.BURGER_FUEL || !site.address.city
              ? "Other"
              : site.address.city;

          return {
            ...groups,
            [key]: [...(groups[key] || []), site],
          };
        },
        {} as Record<string, SiteWithPosition[]>,
      );

    if (
      brandConfig.Brand === Brand.BURGER_FUEL &&
      prevSiteId &&
      filteredSites.length &&
      filteredSites[0].id === prevSiteId
    ) {
      groupedSites[PREV_SITE_SECTION_KEY] = [filteredSites[0]];
    }

    // Sort groups
    const sortedGroupedSites = Object.fromEntries(
      Object.entries(groupedSites).sort(([a], [b]) => {
        if (a === PREV_SITE_SECTION_KEY) {
          return -1;
        } else if (b === PREV_SITE_SECTION_KEY) {
          return 1;
        } else if (sortBy === Sort.A_Z) {
          return a.localeCompare(b);
        } else if (sortBy === Sort.Z_A) {
          return b.localeCompare(a);
        } else if (sortBy === Sort.DISTANCE) {
          // The group's nearest store; a group with no positioned stores goes last
          const nearest = (group: SiteWithPosition[]) =>
            Math.min(
              Infinity,
              ...group.flatMap((site) =>
                site.position
                  ? [
                      distanceInMetersBetween(
                        center?.lat ?? 0,
                        center?.lng ?? 0,
                        site.position.lat,
                        site.position.lng,
                      ),
                    ]
                  : [],
              ),
            );
          const siteADistance = nearest(groupedSites[a]);
          const siteBDistance = nearest(groupedSites[b]);
          if (siteADistance === siteBDistance) return 0;
          return siteADistance < siteBDistance ? -1 : 1;
        }
        return 0;
      }),
    );

    return sortedGroupedSites;
  }, [
    orderSource,
    center?.lat,
    center?.lng,
    searchValue,
    selectedOrderTypeCode,
    selectedRegion,
    sitesWithPosition,
    sortBy,
    prevSiteId,
  ]);

  // Styles
  const backgroundColor = template.storesScreen.general.backgroundColour;
  const titleFontColor = template.storesScreen.general.titleFontColour;
  const titleFontSize =
    StoresTitleFontSize[template.storesScreen.general.titleFontSize];
  const cssVariables: Record<string, string> = {
    "--store-card-stroke-color": hexColorToRGBA(
      template.storesScreen.card.strokeColour,
      template.storesScreen.card.stroke ? 0.25 : 0,
    ),
    "--hover-store-card-stroke-color": hexColorToRGBA(
      template.storesScreen.card.strokeColour,
      template.storesScreen.card.stroke ? 0.35 : 0,
    ),
  };
  const mobileFiltersButtonColor = template.global.button.primaryButtonColour;
  const mobileFiltersIconColor = template.global.button.primaryButtonFontColour;
  // Region styles
  const regionStyles: React.CSSProperties = {
    color: template.menuScreen.category.fontColour,
    fontSize: CategoryFontSize[template.menuScreen.category.fontSize],
    fontFamily: template.menuScreen.category.fontFamily,
    fontWeight: template.menuScreen.category.fontWeight,
    textTransform: template.menuScreen.category.fontTransform,
  };

  return (
    <div className={styles.wrapper}>
      <SiteInfoModal
        site={visibleSiteInfo}
        onClose={() => setVisibleSiteInfo(null)}
      />
      <FiltersDrawer
        sortOptions={sortOptions}
        originalSelectedSort={sortBy}
        regionsOptions={regionsOptions}
        originalSelectedRegion={selectedRegion}
        orderTypesOptions={orderTypesOptions}
        originalSelectedOrderType={selectedOrderTypeCode}
        visible={visibleFiltersDrawer}
        onApply={(sortBy, region, orderTypeCode) => {
          setSortBy(sortBy);
          setSelectedRegion(region);
          setSelectedOrderTypeCode(orderTypeCode);
        }}
        onClear={() => {
          setSortBy(Sort.A_Z);
          setSelectedRegion(null);
          setSelectedOrderTypeCode(null);
        }}
        onClose={() => setVisibleFiltersDrawer(false)}
      />
      <div
        className={styles.header}
        style={{
          backgroundColor,
          color: titleFontColor,
        }}
      >
        <div className={styles.filters}>
          <SearchInput value={searchValue} setValue={setSearchValue} />
          <div className={styles.desktopFilters}>
            <FilterDropdown
              placeholder="All Regions"
              options={regionsOptions}
              value={selectedRegion}
              onChange={setSelectedRegion}
            />
            <FilterDropdown
              placeholder="All Order Types"
              options={orderTypesOptions}
              value={selectedOrderTypeCode}
              onChange={setSelectedOrderTypeCode}
            />
            <FilterDropdown
              placeholder="Sort by A-Z"
              prefixIcon={<SortIcon />}
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
            />
          </div>
          <div
            className={styles.mobileFilters}
            style={{
              backgroundColor: mobileFiltersButtonColor,
            }}
            onClick={() => setVisibleFiltersDrawer(true)}
          >
            <FiltersIcon color={mobileFiltersIconColor} />
          </div>
        </div>
      </div>
      {brandConfig.Brand !== Brand.BURGER_FUEL && (
        <div
          className={styles.title}
          style={{ color: titleFontColor, fontSize: titleFontSize }}
        >
          {Object.values(groupedFilteredAndSortedSites).flat().length}{" "}
          {Object.values(groupedFilteredAndSortedSites).flat().length === 1
            ? "Site"
            : "Sites"}{" "}
          Found
        </div>
      )}
      <div className={styles.content}>
        {Object.keys(groupedFilteredAndSortedSites).map((region) => (
          <div key={region} className={styles.groupSites}>
            {brandConfig.Brand === Brand.BURGER_FUEL && (
              <div className={styles.regionName} style={regionStyles}>
                {region === PREV_SITE_SECTION_KEY ? "Previous Site" : region}
              </div>
            )}
            <div className={styles.sites} style={cssVariables}>
              {groupedFilteredAndSortedSites[region].map((site) => (
                <SiteItem
                  key={site.id}
                  site={site}
                  withMap={false}
                  distance={
                    site.position &&
                    metersToLabel(
                      distanceInMetersBetween(
                        center?.lat ?? 0,
                        center?.lng ?? 0,
                        site.position.lat ?? 0,
                        site.position.lng ?? 0,
                      ),
                    )
                  }
                  setHoveredId={() => {}}
                  onVisibleSiteInfo={() => setVisibleSiteInfo(site)}
                  onSelectSite={() => onSelectSite(site, selectedOrderTypeCode)}
                  isPrevSite={site.id === prevSiteId}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SitesList;
